import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { ApiConfig } from 'config/ApiConfig';
import { toast } from 'react-toastify';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [subdomain, setSubdomain] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // Check if user is already authenticated on mount
  useEffect(() => {
    const storedSubdomain = localStorage.getItem('zendesk_subdomain');
    const storedAuthStatus = localStorage.getItem('xsurvey_auth_status');
    
    if (storedSubdomain && storedAuthStatus === 'true') {
      setSubdomain(storedSubdomain);
      setIsAuthenticated(true);
      setAuthChecked(true);
    } else {
      setAuthChecked(true);
    }
  }, []);

  // Function to set subdomain when user connects Zendesk account
  const setZendeskSubdomain = (newSubdomain) => {

    setSubdomain(newSubdomain);
    localStorage.setItem('zendesk_subdomain', newSubdomain);

  };

  // Function to check authentication status after popup closes
  // Accepts optional subdomainOverride parameter to use when state hasn't updated yet
  const checkAuthenticationStatus = async (subdomainOverride = null) => {
    // Use override first, then state, then localStorage as fallback
    const subdomainToUse = subdomainOverride || subdomain || localStorage.getItem('zendesk_subdomain');

    if (!subdomainToUse) {
      console.error('❌ AuthContext: No subdomain available for authentication check');
      return false;
    }
    setIsLoading(true);

    try {
      const response = await axios.post(ApiConfig.xSurveyAuthCheck, {
        subdomain: subdomainToUse
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });

    
      // Handle the specific response format: {"last_checkup": true}
      const isAuthValid = response.data === true || 
                         response.data.success === true || 
                         response.data.last_checkup === true;
    
      
      setIsAuthenticated(isAuthValid);
      localStorage.setItem('xsurvey_auth_status', isAuthValid.toString());
      
      if (isAuthValid) {
    
        toast.success('Authentication successful!', { theme: 'colored' });
      } else {
     
        toast.error('Authentication failed. Please try again.', { theme: 'colored' });
      }
      
      return isAuthValid;
    } catch (error) {
     
      setIsAuthenticated(false);
      localStorage.setItem('xsurvey_auth_status', 'false');
      
      toast.error(
        error.response?.data?.message || 'Authentication check failed. Please try again.',
        { theme: 'colored' }
      );
      
      return false;
    } finally {
      setIsLoading(false);
   
    }
  };

  // Function to logout/clear authentication
  const logout = () => {
    setIsAuthenticated(false);
    setSubdomain('');
    localStorage.removeItem('zendesk_subdomain');
    localStorage.removeItem('xsurvey_auth_status');
  };

  const value = {
    isAuthenticated,
    subdomain,
    isLoading,
    authChecked,
    setZendeskSubdomain,
    checkAuthenticationStatus,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
